/*
 * Catalogue planning types. The API shapes (Catalogue*Dto, CatalogueProductFeed, CataloguePlan,
 * CatalogueSave*) follow promoflow-api catalogue-api-contract.md; the rest are planner view models.
 */

// ---- API: GET catalogue/promotions/{id}/products ----

/** Decimal values arrive as strings or numbers, or null. */
export type CatalogueDecimal = string | number | null;

export interface CatalogueFeedPrices {
  costExGst: CatalogueDecimal;
  costIncGst: CatalogueDecimal;
  costIncWetOnly: CatalogueDecimal;
  sellPrice: CatalogueDecimal;
  allowance: CatalogueDecimal;
  allowanceGroup: CatalogueDecimal;
  allowancePromotion: CatalogueDecimal;
  allowanceUniversal: CatalogueDecimal;
  allowanceTotal: CatalogueDecimal;
}

export interface CatalogueFeedProductDto {
  productId: number;
  promotionGroupId: number;
  productCode: string;
  productName: string;
  supplierCode: string;
  supplierName: string;
  /** Host classification, for sorting/filtering in the picker only (nullable). */
  departmentDescription?: string | null;
  categoryDescription?: string | null;
  productTypeDescription?: string | null;
  productRange?: string | null;
  warehouseId: number;
  productGroupId: number;
  promotionGroupName: string;
  prices: CatalogueFeedPrices;
}

export interface CatalogueFeedCategoryDto {
  id: number;
  name: string;
  code: number;
  products: CatalogueFeedProductDto[];
}

export interface CatalogueProductFeed {
  promotionHeaderId: number;
  categories: CatalogueFeedCategoryDto[];
}

// ---- API: GET catalogue/promotions/{id} ----

export interface CataloguePlacementPrices {
  costExGst: CatalogueDecimal;
  costIncGst: CatalogueDecimal;
  costIncWetOnly: CatalogueDecimal;
  sellPrice: CatalogueDecimal;
  allowanceTotal: CatalogueDecimal;
}

export interface CataloguePlacementDto {
  productId: number;
  promotionGroupId: number;
  promotionDetailId: number;
  /** Every detail row for this placement (one per included warehouse). */
  promotionDetailIds?: number[];
  position: number;
  productCode: string;
  productName: string;
  prices: CataloguePlacementPrices;
}

export interface CataloguePageDto {
  id: number;
  name: string;
  planningCategoryId: number | null;
  planningCategoryName: string | null;
  capacity: number;
  position: number;
  products: CataloguePlacementDto[];
}

export interface CataloguePlan {
  promotionHeaderId: number;
  version: number;
  canEdit: boolean;
  readOnlyReason: string;
  pages: CataloguePageDto[];
}

// ---- API: PUT catalogue/promotions/{id} ----

export interface CatalogueSaveRequest {
  expectedVersion: number;
  pages: Array<{
    id?: number | string;
    name: string;
    planningCategoryId: number | null;
    capacity: number;
    position: number;
    products: Array<{ productId: number; promotionGroupId: number; position: number }>;
  }>;
}

/** The contract's PUT response contains only the header id and the new version. */
export interface CatalogueSaveResponse {
  promotionHeaderId: number;
  version: number;
}

/** Save limits enforced by the API's PUT validation. */
export const CATALOGUE_LIMITS = Object.freeze({
  maxPages: 200,
  maxProductsPerPage: 1000,
  maxPlacements: 5000,
  maxCapacity: 65535,
  maxPageNameLength: 100
});

// ---- Planner view models ----

/** A product the planner can place. Prices are deliberately not carried: the planner doesn't show them. */
export interface CatalogueProduct {
  productId: number;
  promotionGroupId: number;
  code: string;
  name: string;
  planningCategoryId: number;
  category: string;
  /** Saved placement that is no longer in the eligible-product feed. */
  ineligible?: boolean;
  /** Feed-only display fields for the product-selection grid. */
  supplierName?: string;
  promotionGroupName?: string;
  departmentDescription?: string;
  categoryDescription?: string;
  productTypeDescription?: string;
  productRange?: string;
}

export interface CataloguePlacement {
  productId: number;
  promotionGroupId: number;
  promotionDetailId?: number;
  position?: number;
  /** Saved metadata, shown when the product is no longer in the eligible feed. */
  productCode?: string;
  productName?: string;
  planningCategoryName?: string;
}

export interface CataloguePage {
  id: number | string;
  name: string;
  planningCategoryId: number | null;
  category: string;
  capacity: number;
  products: CataloguePlacement[];
  position?: number;
}

// ---- Planning method ----

export type PlanningMethod = 'slotting' | 'catalogue';

export interface CataloguePlanningMethod {
  method: PlanningMethod;
  canManage: boolean;
}

/** An open promotion holding the other method's detail lines, which blocks a method switch (409). */
export interface BlockingPromotion {
  id: number;
  description: string;
  promotion_start: string;
  promotion_end: string;
  status: string;
  statusType: string | null;
}

/** `data` of the 409 returned by `PUT catalogue/planning-method` when promotions block the switch. */
export interface PlanningMethodConflictData {
  blockingPromotions: BlockingPromotion[];
  blockingCount: number;
}
